// The application inviting itself to be installed (D-208).
//
// Installed, the field application opens from the home screen with the
// organization's own icon and at full screen, like any other application of
// the tablet, with none of the browser's bars eating the working area. That
// is the whole argument, and it is worth making once, where it can be made.
//
// Where it can be made is not the same everywhere, and this is the one place
// that knows the difference:
//
//   * a browser that offers the installation itself — Chrome on Android —
//     hands the page an event it must keep, because the invitation opens the
//     browser's own dialog with it and nothing else can;
//   * Safari on an iPad installs from its share menu and offers the page
//     nothing at all, so the invitation is the two steps a person follows;
//   * already installed, or anywhere else, there is nothing to invite to and
//     the invitation does not exist (D-191: what does not apply is not there,
//     not there and disabled).
//
// B-50 (D-435): and on Android the application is installed from Chrome
// alone. Chrome wraps the installed application in a package Google signs,
// which Play Protect lets through; Samsung Internet wraps its own, which Play
// Protect stopped on a tablet of staging. And what is kept for working without
// signal lives in the browser it is worked in: two browsers of one tablet are
// two copies that never see each other. So on Android, in any browser but
// Chrome, the invitation leads to Chrome — whether that browser offers to
// install or not, and its offer, when it makes one, is kept from it and never
// used. The application still works in that browser exactly as before: what
// changes is only where it is installed.
//
// Chrome makes its offer when it is ready to, which on the first visit is
// usually before anybody signed in. The offer is heard from the page's first
// moment, for whoever signs in, and only heard: it is taken from the browser
// — kept for the invitation, the browser's own bar left out — once the
// field's runtime starts for an operator, and never for the office or the
// portal, which see what they always saw.
//
// The dismissal is the device's and survives the sign out, like the device's
// own identifier: the operator said no on this tablet, and asking again at
// every sign in would be asking again forever. The way to install stays on
// the device's screen, where it is looked for rather than offered.
import type { FieldcoreDb } from '../db/database';

/** What the device can do about installing, right now. */
export type InstallWay =
  /** The browser offers the installation and gave us the way to open it. */
  | 'prompt'
  /** Safari on an iPad or an iPhone: the share menu, in two steps. */
  | 'ios'
  /** B-50 (D-435): Android in a browser that is not Chrome, which is where
   *  the invitation leads. */
  | 'chrome'
  /** Already installed, or a browser that does not install at all. */
  | 'none';

export interface InstallSnapshot {
  /** Running as an application of its own rather than inside a browser. */
  standalone: boolean;
  way: InstallWay;
  /** The operator dismissed the invitation on this device. */
  dismissed: boolean;
}

/** The event Chromium hands the page instead of installing by itself. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallServiceOptions {
  db: FieldcoreDb;
  /** The window the service listens on; tests give their own. */
  target?: Pick<Window, 'addEventListener' | 'removeEventListener' | 'matchMedia'> | null;
  /** How the device names itself, and whether it says it is standalone.
   *  B-50: and the client hints it declares, where it declares them. */
  navigatorLike?: {
    userAgent: string;
    maxTouchPoints: number;
    standalone?: boolean;
    userAgentData?: unknown;
  } | null;
}

type Listener = (snapshot: InstallSnapshot) => void;

function browserNavigator(): { userAgent: string; maxTouchPoints: number } | null {
  if (typeof navigator === 'undefined') return null;
  return navigator;
}

/** What a browser declares of itself (B-50): its agent string always, and the
 *  client hints where it has them, which a browser fills for itself rather
 *  than copying another's. */
export interface DeclaredBrowser {
  userAgent: string;
  userAgentData?: { platform: string; brands: readonly { brand: string }[] } | null;
}

/** On Android, whether the browser is Chrome; anywhere else, that it is not
 *  Android, which leaves everything as it was. */
export type AndroidBrowser = 'chrome' | 'other' | 'not_android';

/** The brand Chrome declares among its client hints, and nobody else does. */
const CHROME_BRAND = 'Google Chrome';

/** The marks another browser leaves in its agent string, beside Chrome's own
 *  token where it carries one, the system's view of another application
 *  (`; wv`) included. Read only where the browser declares no client hints. */
const NOT_CHROME_MARKS: readonly string[] = [
  'SamsungBrowser',
  'EdgA',
  'OPR',
  'Firefox',
  'YaBrowser',
  'MiuiBrowser',
  'UCBrowser',
  'HuaweiBrowser',
  'DuckDuckGo',
  '; wv',
];

/** B-50 (D-435): whether this is Android, and on Android whether it is
 *  Chrome, from what the browser declares and nothing else.
 *
 *  Chrome is recognised in the positive. Where the browser declares client
 *  hints they decide: Chrome is the browser whose brands name it, whatever its
 *  agent string says, so one that copies Chrome's string and does not declare
 *  Chrome's brand is not Chrome. Where it declares none, the agent string
 *  must carry Chrome's token and none of the marks another browser adds, nor
 *  the `Version/` the system's view of another application puts before it.
 *  Everything else on Android is another browser, the one inside another
 *  application included; Chrome opened by another application on its behalf
 *  (a Custom Tab) declares itself Chrome and is Chrome. */
export function androidBrowserOf(declared: DeclaredBrowser): AndroidBrowser {
  const hints = declared.userAgentData ?? null;
  const agent = declared.userAgent;
  const android = hints !== null ? hints.platform === 'Android' : agent.includes('Android');
  if (!android) return 'not_android';
  if (hints !== null) {
    return hints.brands.some((entry) => entry.brand === CHROME_BRAND) ? 'chrome' : 'other';
  }
  const chrome = agent.indexOf('Chrome/');
  if (chrome < 0) return 'other';
  if (NOT_CHROME_MARKS.some((mark) => agent.includes(mark))) return 'other';
  const version = agent.indexOf('Version/');
  if (version >= 0 && version < chrome) return 'other';
  return 'chrome';
}

/** The client hints of a navigator, read as the shape `androidBrowserOf`
 *  takes, or none where the browser declares none or something else. */
function hintsOf(value: unknown): DeclaredBrowser['userAgentData'] {
  if (typeof value !== 'object' || value === null) return null;
  const platform: unknown = Reflect.get(value, 'platform');
  const brands: unknown = Reflect.get(value, 'brands');
  if (typeof platform !== 'string' || !Array.isArray(brands)) return null;
  return {
    platform,
    brands: brands.map((entry: unknown) => {
      const brand: unknown =
        typeof entry === 'object' && entry !== null ? Reflect.get(entry, 'brand') : null;
      return { brand: typeof brand === 'string' ? brand : '' };
    }),
  };
}

/** Where the installed application starts: the manifest's `start_url`
 *  (api/src/app/services/app_public.py), which the address sent to Chrome
 *  repeats so that Chrome installs what it opens. */
export const START_PATH = '/';

/** This application's address, as a person pastes it in Chrome: the scheme
 *  and the host of the address in use, its port included, and the start. */
export function appAddressOf(location: Pick<Location, 'protocol' | 'host'>): string {
  return `${location.protocol}//${location.host}${START_PATH}`;
}

/** The link Android follows to open that same address in Chrome. Where
 *  Chrome is not on the device, Android offers to install it; nothing else is
 *  offered in its place. */
export function chromeIntentOf(location: Pick<Location, 'protocol' | 'host'>): string {
  const scheme = location.protocol.replace(/:$/, '');
  return `intent://${location.host}${START_PATH}#Intent;scheme=${scheme};package=com.android.chrome;end`;
}

/** Safari on an iPad or an iPhone. iPadOS calls itself a Macintosh since 13,
 *  and only the touch points tell it from a desktop: a desktop Safari and a
 *  Mac with a trackpad both report none, and every touch screen reports at
 *  least one. */
export function isAppleTablet(agent: { userAgent: string; maxTouchPoints: number }): boolean {
  const ua = agent.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return ua.includes('Macintosh') && agent.maxTouchPoints > 0;
}

export class InstallService {
  private readonly db: FieldcoreDb;
  private readonly target: InstallServiceOptions['target'];
  private readonly agent: InstallServiceOptions['navigatorLike'];
  private deferred: InstallPromptEvent | null = null;
  private snapshot: InstallSnapshot = { standalone: false, way: 'none', dismissed: false };
  private readonly listeners = new Set<Listener>();
  /** B-50: hearing the browser's offer, from `listen()` on. */
  private listening = false;
  /** B-50: taking it from the browser, while the field's runtime runs. */
  private intercepting = false;
  /** Whether the dismissal the device keeps was read: until then there is no
   *  way to offer, so a dismissed invitation never shows for a moment. */
  private ready = false;

  private readonly onBeforeInstallPrompt = (event: Event): void => {
    // Kept rather than let through: without it the invitation has no dialog
    // to open, and the browser's own bar is not the invitation we want.
    // B-50: taken from the browser only for the field; before an operator's
    // session, or for another role's, the offer is heard and the browser
    // keeps its own.
    if (this.intercepting) event.preventDefault();
    this.deferred = event as InstallPromptEvent;
    this.recompute();
  };

  private readonly onInstalled = (): void => {
    this.deferred = null;
    this.recompute();
  };

  constructor(options: InstallServiceOptions) {
    this.db = options.db;
    this.target = options.target === undefined ? globalThis.window : options.target;
    this.agent = options.navigatorLike === undefined ? browserNavigator() : options.navigatorLike;
  }

  getSnapshot(): InstallSnapshot {
    return this.snapshot;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** B-50 (D-435): hear the browser's offer from the page's first moment, so
   *  that one made before anybody signed in is there for the operator who
   *  signs in. Hearing it takes nothing from the browser. Once is enough: the
   *  page hears it for as long as it lives. */
  listen(): void {
    if (this.listening) return;
    this.listening = true;
    this.target?.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.target?.addEventListener('appinstalled', this.onInstalled);
  }

  /** Start listening and read the dismissal the device keeps. B-50: and take
   *  the offer from the browser from now on, for the field's invitation. */
  async start(): Promise<void> {
    this.listen();
    this.intercepting = true;
    const record = await this.db.session.get('install');
    this.ready = true;
    this.recompute(record?.key === 'install' ? record.dismissed : false);
  }

  /** B-50: the field's runtime stopped: the offer is heard again, not taken,
   *  as before any operator's session. */
  stop(): void {
    this.intercepting = false;
  }

  /** Running as an application of its own: the browser says so through the
   *  display mode, and Safari through a flag of its own. */
  private isStandalone(): boolean {
    const modes = ['standalone', 'fullscreen', 'minimal-ui'];
    for (const mode of modes) {
      try {
        if (this.target?.matchMedia(`(display-mode: ${mode})`).matches === true) return true;
      } catch {
        // A target without matchMedia says nothing; the flag below may.
      }
    }
    const agent = this.agent;
    if (agent === null || agent === undefined) return false;
    const standalone: unknown = Reflect.get(agent, 'standalone');
    return standalone === true;
  }

  private recompute(dismissed = this.snapshot.dismissed): void {
    const standalone = this.isStandalone();
    const way: InstallWay = standalone || !this.ready ? 'none' : this.wayOf();
    this.snapshot = { standalone, way, dismissed };
    for (const listener of this.listeners) listener(this.snapshot);
  }

  /** The way in, in its order (B-50, D-435): Android outside Chrome leads to
   *  Chrome before anything the browser offers; then the browser's own
   *  dialog, where it offered one; then Safari's two steps. */
  private wayOf(): InstallWay {
    const agent = this.agent ?? null;
    const declared: DeclaredBrowser | null =
      agent === null
        ? null
        : {
            userAgent: agent.userAgent,
            userAgentData: hintsOf(Reflect.get(agent, 'userAgentData')),
          };
    if (declared !== null && androidBrowserOf(declared) === 'other') return 'chrome';
    if (this.deferred !== null) return 'prompt';
    if (agent !== null && isAppleTablet(agent)) return 'ios';
    return 'none';
  }

  /** Open the browser's own installation dialog. Answers whether it was
   *  accepted; either way the event is spent and cannot be used again.
   *  B-50: only where that dialog is the way in: an offer kept from another
   *  browser of Android is never used. */
  async install(): Promise<boolean> {
    const event = this.deferred;
    if (event === null || this.snapshot.way !== 'prompt') return false;
    this.deferred = null;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      this.recompute();
      return choice.outcome === 'accepted';
    } catch {
      this.recompute();
      return false;
    }
  }

  /** The operator said no on this device: the invitation does not come back. */
  async dismiss(): Promise<void> {
    await this.db.session.put({ key: 'install', dismissed: true, at: Date.now() });
    this.recompute(true);
  }
}
